/**
 * O texto de uma mensagem, transformado em nós de React.
 *
 * NUNCA passa por `dangerouslySetInnerHTML`: o conteúdo vem de outro
 * utilizador, e um render de HTML cru é XSS armazenado — a vulnerabilidade
 * clássica dos clientes de chat. Ao construir nós de React, o próprio React
 * escapa tudo o que é texto, e a superfície de ataque desaparece por desenho,
 * não por sanitização (que é sempre uma corrida que se perde).
 *
 * Suporta o subconjunto que as pessoas realmente escrevem: blocos de código,
 * código inline, negrito, itálico, sublinhado, rasurado, spoiler, links e
 * menções. O resto é texto.
 */

import { Fragment, type ReactNode, useState } from "react";

import { useStore } from "../store/store";
import { EmojiImage } from "./EmojiGlyph";

const FENCE_SOURCE = "```(?:([a-zA-Z0-9+#-]{1,16})\\n)?([\\s\\S]*?)```";

const INLINE_SOURCE = [
  "(`[^`\\n]+`)", // 1 código inline
  "(\\*\\*\\*[^*]+\\*\\*\\*)", // 2 negrito+itálico
  "(\\*\\*[^*]+\\*\\*)", // 3 negrito
  "(\\*[^*\\n]+\\*)", // 4 itálico
  "(__[^_]+__)", // 5 sublinhado
  "(~~[^~]+~~)", // 6 rasurado
  "(\\|\\|[\\s\\S]+?\\|\\|)", // 7 spoiler
  "(<@\\d+>)", // 8 menção a pessoa
  "(<#\\d+>)", // 9 menção a canal
  "(<a?:[a-zA-Z0-9_]{2,32}:\\d+>)", // 10 emoji personalizado
  "(https?://[^\\s<]+)", // 11 link
].join("|");

/**
 * O `a` opcional do `<a:nome:id>` é o dialecto que toda a gente já conhece de
 * outros clientes, e alguém vai colá-lo aqui. Aceita-se à entrada e ignora-se:
 * quem decide se um emoji é animado é o servidor, a partir do tipo do ficheiro
 * — a letra colada no texto não é prova de nada.
 */
const EMOJI_TOKEN = /^<a?:([a-zA-Z0-9_]+):(\d+)>$/;

/**
 * Um regex NOVO por chamada, de propósito: `inline` é recursivo (negrito dentro
 * de itálico) e um `/g` partilhado leva o `lastIndex` da chamada de dentro para
 * a de fora — o género de bug que só aparece na mensagem do cliente.
 */
const fence = (): RegExp => new RegExp(FENCE_SOURCE, "g");
const inlineRe = (): RegExp => new RegExp(INLINE_SOURCE, "g");

export function RichText({ content }: { content: string }) {
  if (!content) return null;

  const blocks: ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;

  const re = fence();
  while ((match = re.exec(content)) !== null) {
    if (match.index > cursor) {
      blocks.push(<Fragment key={`t${cursor}`}>{inline(content.slice(cursor, match.index))}</Fragment>);
    }
    const language = match[1];
    const code = (match[2] ?? "").replace(/^\n/, "").replace(/\n$/, "");
    blocks.push(
      <pre key={`c${match.index}`} className="code-block">
        {language ? <span className="code-block__lang">{language}</span> : null}
        <code>{code}</code>
      </pre>,
    );
    cursor = match.index + match[0].length;
  }

  if (cursor < content.length) {
    blocks.push(<Fragment key={`t${cursor}`}>{inline(content.slice(cursor))}</Fragment>);
  }

  return <>{blocks}</>;
}

function inline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;

  const re = inlineRe();
  while ((match = re.exec(text)) !== null) {
    if (match.index > cursor) nodes.push(...plain(text.slice(cursor, match.index), cursor));
    const key = `m${match.index}`;
    const [raw] = match;

    if (match[1])
      nodes.push(
        <code key={key} className="code-inline">
          {strip(raw, 1)}
        </code>,
      );
    else if (match[2])
      nodes.push(
        <strong key={key}>
          <em>{inline(strip(raw, 3))}</em>
        </strong>,
      );
    else if (match[3]) nodes.push(<strong key={key}>{inline(strip(raw, 2))}</strong>);
    else if (match[4]) nodes.push(<em key={key}>{inline(strip(raw, 1))}</em>);
    else if (match[5]) nodes.push(<u key={key}>{inline(strip(raw, 2))}</u>);
    else if (match[6]) nodes.push(<s key={key}>{inline(strip(raw, 2))}</s>);
    else if (match[7]) nodes.push(<Spoiler key={key} text={strip(raw, 2)} />);
    else if (match[8]) nodes.push(<UserMention key={key} userId={raw.slice(2, -1)} />);
    else if (match[9]) nodes.push(<ChannelMention key={key} channelId={raw.slice(2, -1)} />);
    else if (match[10]) {
      const token = EMOJI_TOKEN.exec(raw);
      // O regex de cima e este são o mesmo desenho, mas o `exec` pode falhar se
      // um dia divergirem; sem o ramo, um `null` rebentava o render da mensagem.
      nodes.push(
        token ? (
          <CustomEmoji key={key} name={token[1] ?? ""} emojiId={token[2] ?? ""} />
        ) : (
          <Fragment key={key}>{raw}</Fragment>
        ),
      );
    } else if (match[11]) nodes.push(<Link key={key} href={raw} />);

    cursor = match.index + raw.length;
  }

  if (cursor < text.length) nodes.push(...plain(text.slice(cursor), cursor));
  return nodes;
}

/** Texto simples com as quebras de linha preservadas. */
function plain(text: string, offset: number): ReactNode[] {
  const lines = text.split("\n");
  const nodes: ReactNode[] = [];
  lines.forEach((line, index) => {
    if (index > 0) nodes.push(<br key={`b${offset}-${index}`} />);
    if (line) nodes.push(line);
  });
  return nodes;
}

const strip = (text: string, size: number): string => text.slice(size, text.length - size);

function Spoiler({ text }: { text: string }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <span
      className={`spoiler${revealed ? " is-open" : ""}`}
      role="button"
      tabIndex={0}
      onClick={() => setRevealed(true)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") setRevealed(true);
      }}
    >
      {revealed ? inline(text) : "spoiler"}
    </span>
  );
}

function UserMention({ userId }: { userId: string }) {
  const user = useStore((s) => s.users[userId]);
  const isMe = useStore((s) => s.self?.id === userId);
  return (
    <span className={`mention${isMe ? " mention--me" : ""}`}>
      @{user ? (user.displayName ?? user.username) : "desconhecido"}
    </span>
  );
}

function ChannelMention({ channelId }: { channelId: string }) {
  const channel = useStore((s) => s.channels[channelId]);
  const selectChannel = useStore((s) => s.selectChannel);
  if (!channel) return <span className="mention">#desconhecido</span>;
  return (
    <button type="button" className="mention" onClick={() => selectChannel(channelId)}>
      #{channel.name}
    </button>
  );
}

/**
 * Um emoji personalizado.
 *
 * Quando o id não está no store — o emoji foi apagado, ou é de um servidor onde
 * não estamos — mostra-se `:nome:` em texto. É por isso que o nome vai no
 * token e não só o id: sem ele, uma mensagem antiga ficava com um buraco em vez
 * de dizer o que lá esteve. O `alt` faz o mesmo por quem usa leitor de ecrã e
 * pela imagem que não carrega.
 */
function CustomEmoji({ name, emojiId }: { name: string; emojiId: string }) {
  const emoji = useStore((s) => s.emojis[emojiId]);
  if (!emoji) return <>:{name}:</>;
  return <EmojiImage url={emoji.url} name={emoji.name} />;
}

/**
 * `noopener` é obrigatório: sem ele a página aberta recebe `window.opener` e
 * pode reescrever a nossa aba (tabnabbing). `noreferrer` evita fugir o URL
 * interno no cabeçalho Referer.
 */
function Link({ href }: { href: string }) {
  let label = href;
  try {
    const url = new URL(href);
    label = url.host + (url.pathname === "/" ? "" : url.pathname);
  } catch {
    // URL inválido apanhado pelo regex: mostra-se cru, sem link.
    return <>{href}</>;
  }
  return (
    <a className="link-out" href={href} target="_blank" rel="noopener noreferrer" title={href}>
      {label}
    </a>
  );
}
